"use client";

import { Api } from "@/app/api";
import { CreatePlanDto, EditPlanDto, Plan, PlanStatus, UserPlan } from "@/types";
import { Currency } from "@/lib/utils/currency";

export const planInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getPlans: builder.query<
			Plan[],
			{
				status?: PlanStatus;
				isCustomPlan?: boolean;
				sortBy?: `${"createdAt" | "price"}:${"asc" | "desc"}`;
			}
		>({
			query: ({ status, isCustomPlan, sortBy }) => ({
				url: "/plans",
				headers: {
					"Content-Type": "application/json",
				},
				params: {
					status,
					isCustomPlan,
					sortBy,
				},
			}),
			providesTags: ["plans"],
		}),
		getPlan: builder.query<Plan, string>({
			query: (id) => ({
				url: `/plans/${id}`,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["plans"],
		}),
		createCustomOrGenericPlan: builder.mutation<void, CreatePlanDto>({
			query: (body) => ({
				url: "/plans",
				method: "POST",
				body: body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
		}),
		activatePlan: builder.mutation<
			{ checkoutUrl: string },
			{ id: string; duration: UserPlan; currency?: Currency }
		>({
			query: ({ id, duration, currency }) => ({
					url: `/plans/${id}/activate`,
					method: "POST",
				body: { duration: duration, currency: currency },
					headers: {
						"Content-Type": "application/json",
					...(currency && { "x-currency": currency }),
					},
				}),
		}),
		upgradePlan: builder.mutation<
			{ checkoutUrl: string },
			{ id: string; duration: UserPlan; currency?: Currency }
		>({
			query: ({ id, duration, currency }) => ({
				url: `/plans/${id}/upgrade`,
				method: "POST",
				body: { duration: duration, currency: currency },
				headers: {
					"Content-Type": "application/json",
					...(currency && { "x-currency": currency }),
				},
			}),
		}),
		downgradePlan: builder.mutation<
			{ message: string; planName: string; effectiveAt: string },
			{ id: string; duration: UserPlan; currency?: Currency }
		>({
			query: ({ id, duration, currency }) => ({
				url: `/plans/${id}/downgrade`,
				method: "POST",
				body: { duration, currency },
				headers: {
					"Content-Type": "application/json",
					...(currency && { "x-currency": currency }),
				},
			}),
			invalidatesTags: ["user", "order"],
		}),
		syncMembership: builder.mutation<{ ok: true }, void>({
			query: () => ({
				url: `/plans/sync-membership`,
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["user"],
		}),
		updatePlan: builder.mutation<{ checkoutUrl: string }, { id: string; body: EditPlanDto }>({
			query: ({ id, body }) => ({
				url: `/plans/${id}`,
				method: "PATCH",
				body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["plans"],
		}),
	}),
});

export const {
	useActivatePlanMutation,
	useUpgradePlanMutation,
	useDowngradePlanMutation,
	useSyncMembershipMutation,
	useCreateCustomOrGenericPlanMutation,
	useUpdatePlanMutation,
	useGetPlansQuery,
	useGetPlanQuery,
} = planInjection;
